import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { XAxisButton } from '@/common/dashboard/XAxisButton';
import { Mode } from '@/common/entityFramework/enumerations/Mode';
import { useForm } from '@/common/form/FormContext';
import { useNavState } from '@/common/navState/NavStateContext';
import { ExprUtils } from '@/common/utils/ExprUtils';
import { useMemo } from 'react';

export const DirSaleTrendCard = ({ xAxisOptions, metricOptions }) => {
  const { formValues } = useForm();
  const { navigateTo } = useNavState();

  const reqParam = useMemo(
    () => ({
      asOfPeriodId: formValues.asOfPeriodId,
      periodicity: formValues.periodicity,
      duration: formValues.duration,
      metric: formValues.metric,
      prodExpr: formValues.prodExpr,
      custExpr: formValues.custExpr,
      soldCustExpr: formValues.soldCustExpr,
      invPeriodExpr: formValues.invPeriodExpr,
      xAxis: formValues.xAxis,
    }),
    [
      formValues.asOfPeriodId,
      formValues.periodicity,
      formValues.duration,
      formValues.metric,
      formValues.prodExpr,
      formValues.custExpr,
      formValues.soldCustExpr,
      formValues.invPeriodExpr,
      formValues.xAxis,
    ],
  );

  const navigateToDirSaleLineList = (data, point) => {
    const id = data.ids[point.x];
    const navState = {
      mode: Mode.LIST,
      expr: ExprUtils.and(ExprUtils.whereParams(data.drillField, id), data.filterExpr),
    };

    navigateTo('sc/dir-sale-line', navState);
  };

  const metric =
    metricOptions
      .filter((option) => formValues.metric?.includes(option.value))
      .map((option) => option.displayName)
      .join(' & ') || metricOptions[0].displayName;
  const xAxisLabel = xAxisOptions.find((option) => option.code === formValues.xAxis)?.displayName;

  // Each axis is titled after the series plotted against it (the plugin assigns yAxis per metric)
  const axisLabels = (data) => {
    const labels = [];
    data.series.forEach((seriesItem) => {
      labels[seriesItem.yAxis ?? 0] ??= seriesItem.label;
    });
    return labels;
  };

  return (
    <DashboardCard
      title={metric + ' by ' + xAxisLabel}
      description={'Displays the Direct Sale Lines ' + metric + ' by ' + xAxisLabel}
      module="sc"
      plugin="DIR_SALE_TREND_DATA_PLUGIN"
      reqParam={reqParam}
      buttons={<XAxisButton options={xAxisOptions} />}
    >
      {(data) => {
        const [yLabel, y2Label] = axisLabels(data);
        return (
          <CartesianChart
            rotateXLabels={false}
            onClick={(point) => navigateToDirSaleLineList(data, point)}
            data={data}
            legend="top"
            yLabel={yLabel ?? metric}
            y2Label={y2Label}
            allowNegative
          />
        );
      }}
    </DashboardCard>
  );
};
